import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, Trash2, Loader2, RefreshCw, Users, Crown, UserPlus, UserCog,
  Copy, CheckCircle2, KeyRound,
} from 'lucide-react';
import { formatApiError } from '../lib/errors';

export default function SuperAdmin() {
  const { token, user, beginImpersonation } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(null);

  // Add-user dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', is_super_admin: false });
  const [creating, setCreating] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null); // {email, temp_password}
  const [copiedField, setCopiedField] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminListTenants(token);
      setTenants(r.data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Only super admins can view this page');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const remove = async (t) => {
    if (!window.confirm(`Delete tenant ${t.email} and ALL their data? This cannot be undone.`)) return;
    setBusy(t.id);
    try {
      await api.adminDeleteTenant(token, t.id);
      toast.success(`Deleted ${t.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(null); }
  };

  const promote = async (t) => {
    setBusy(t.id);
    try {
      await api.adminPromote(token, t.id);
      toast.success(`${t.email} is now a super admin`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Promote failed');
    } finally { setBusy(null); }
  };

  const sendReset = async (t) => {
    if (!window.confirm(`Email ${t.email} a link to set a new password? Their current password keeps working.`)) return;
    setBusy(t.id);
    try {
      await api.adminSendReset(token, t.id);
      toast.success(`Reset link sent to ${t.email}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not send the reset link'));
    } finally { setBusy(null); }
  };

  const impersonate = async (t) => {
    setBusy(t.id);
    try {
      await beginImpersonation(t.id);
      toast.success(`Impersonating ${t.email} — banner will stay until you exit`);
      // Full page reload avoids any stale super-admin fetches (SuperAdmin
      // still mounted) triggering a 403 with the new tenant-scoped token.
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impersonation failed');
      setBusy(null);
    }
  };

  const createUser = async () => {
    if (!addForm.email || !addForm.name) {
      toast.error('Email and name are required');
      return;
    }
    setCreating(true);
    try {
      const r = await api.adminCreateUser(token, addForm);
      setCreatedCreds({
        email: r.data.user.email,
        temp_password: r.data.temp_password,
      });
      toast.success(`Created ${r.data.user.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const closeAddDialog = () => {
    setAddOpen(false);
    setAddForm({ email: '', name: '', is_super_admin: false });
    setCreatedCreds(null);
    setCopiedField(null);
  };

  const copy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  if (!user?.is_super_admin && !loading) {
    return (
      <div className="p-8 text-center text-slate-400">
        <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-50"/>
        This page requires super-admin access.
      </div>
    );
  }

  const connectedCount = tenants.filter((t)=>t.untill_verified && (t.qbo_connected || t.odoo_connected)).length;
  const liveCount = tenants.filter((t)=>t.go_live_enabled).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Crown className="w-6 h-6 text-amber-300"/>
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">Super Admin</h1>
            <p className="text-slate-400 mt-1">Every tenant on this deployment. Handle with care.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAddOpen(true)} className="bg-blue-500 hover:bg-blue-500/90 text-white" data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2"/> Add user
          </Button>
          <Button onClick={load} variant="outline" className="border-slate-700 text-slate-200 hover:text-white" data-testid="refresh-tenants-btn">
            <RefreshCw className="w-4 h-4 mr-2"/> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Tenants" value={tenants.length}/>
        <Stat label="Fully connected" value={connectedCount} accent="emerald"/>
        <Stat label="Real-time live" value={liveCount} accent="blue"/>
        <Stat label="Super admins" value={tenants.filter((t)=>t.is_super_admin).length} accent="amber"/>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-300"/> All tenants
          </CardTitle>
          <CardDescription className="text-slate-500">Impersonate to troubleshoot in the tenant&apos;s shoes. Deletion removes the user and every one of their linked records.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto"/></div>
          ) : (
            <div className="rounded-lg border border-slate-800 overflow-x-auto" data-testid="tenants-table-scroll">
              <div className="min-w-[860px]">
              <div className="grid grid-cols-12 px-3 py-2 bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-500">
                <div className="col-span-3">Email</div>
                <div className="col-span-2">UnTill</div>
                <div className="col-span-2">QuickBooks</div>
                <div className="col-span-2">Odoo</div>
                <div className="col-span-1">Live</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <div className="divide-y divide-slate-800/70">
                {tenants.map((t)=>(
                  <div key={t.id} className="grid grid-cols-12 px-3 py-2 items-center text-sm" data-testid={`tenant-${t.id}`}>
                    <div className="col-span-3 truncate">
                      <div className="text-white flex items-center gap-2 min-w-0" title={t.email}>
                        <span className="truncate">{t.email}</span>
                        {t.is_super_admin && (
                          <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[9px] px-1.5 py-0">SUPER</Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">{t.id}</div>
                    </div>
                    <div className="col-span-2 text-xs">
                      {t.untill_verified
                        ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{t.untill_site || t.untill_tenant || 'connected'}</Badge>
                        : <span className="text-slate-500">—</span>}
                    </div>
                    <div className="col-span-2 text-xs">
                      {t.qbo_connected
                        ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{t.qbo_company || 'connected'}</Badge>
                        : <span className="text-slate-500">—</span>}
                    </div>
                    <div className="col-span-2 text-xs">
                      {t.odoo_connected
                        ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{t.odoo_company || 'connected'}</Badge>
                        : <span className="text-slate-500">—</span>}
                    </div>
                    <div className="col-span-1">
                      {t.go_live_enabled
                        ? <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px]">LIVE</Badge>
                        : <span className="text-slate-500 text-xs">off</span>}
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      {t.id !== user?.id && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-300 hover:text-white hover:bg-blue-500/10"
                          onClick={()=>impersonate(t)} disabled={busy===t.id} data-testid={`impersonate-${t.id}`}
                          title="Impersonate this tenant">
                          {busy===t.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <UserCog className="w-3 h-3"/>}
                        </Button>
                      )}
                      {!t.is_super_admin && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-300 hover:text-white hover:bg-amber-500/10"
                          onClick={()=>promote(t)} disabled={busy===t.id} data-testid={`promote-${t.id}`}
                          title="Promote to super admin">
                          <Crown className="w-3 h-3"/>
                        </Button>
                      )}
                      {t.id !== user?.id && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-300 hover:text-white hover:bg-emerald-500/10"
                          onClick={()=>sendReset(t)} disabled={busy===t.id} data-testid={`send-reset-${t.id}`}
                          title="Email a reset password link" aria-label={`Reset password for ${t.email}`}>
                          <KeyRound className="w-3 h-3"/>
                        </Button>
                      )}
                      {t.id !== user?.id && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-300 hover:text-white hover:bg-rose-500/10"
                          onClick={()=>remove(t)} disabled={busy===t.id} data-testid={`delete-${t.id}`}
                          title="Delete tenant + all data">
                          {busy===t.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <Trash2 className="w-3 h-3"/>}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={(o)=>{ if(!o) closeAddDialog(); else setAddOpen(true); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-lg" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {createdCreds ? 'Share these one-time credentials' : 'Add a new user'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {createdCreds
                ? 'This password is shown only now. Send it to the user through a secure channel — they can change it after signing in.'
                : 'Creates the user directly with a temporary password. No email is sent.'}
            </DialogDescription>
          </DialogHeader>

          {!createdCreds ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="user-email" className="text-slate-300">Email</Label>
                <Input id="user-email" type="email" value={addForm.email}
                  onChange={(e)=>setAddForm({...addForm, email: e.target.value})}
                  placeholder="pilot@example.com"
                  className="bg-slate-950 border-slate-800 text-white mt-1.5"
                  data-testid="add-user-email"/>
              </div>
              <div>
                <Label htmlFor="user-name" className="text-slate-300">Full name</Label>
                <Input id="user-name" value={addForm.name}
                  onChange={(e)=>setAddForm({...addForm, name: e.target.value})}
                  placeholder="Pilot Merchant"
                  className="bg-slate-950 border-slate-800 text-white mt-1.5"
                  data-testid="add-user-name"/>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                <input type="checkbox" checked={addForm.is_super_admin}
                  onChange={(e)=>setAddForm({...addForm, is_super_admin: e.target.checked})}
                  className="rounded border-slate-700 bg-slate-950"
                  data-testid="add-user-super"/>
                Grant super-admin access
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              <CopyRow label="Email" value={createdCreds.email} isCopied={copiedField==='email'} onCopy={()=>copy('email', createdCreds.email)} testId="created-email"/>
              <CopyRow label="Temporary password" value={createdCreds.temp_password} isCopied={copiedField==='password'} onCopy={()=>copy('password', createdCreds.temp_password)} testId="created-password"/>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!createdCreds ? (
              <>
                <Button variant="outline" onClick={closeAddDialog} className="border-slate-700 text-slate-200">Cancel</Button>
                <Button onClick={createUser} disabled={creating} className="bg-blue-500 hover:bg-blue-500/90 text-white" data-testid="create-user-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <UserPlus className="w-4 h-4 mr-2"/>}
                  Create user
                </Button>
              </>
            ) : (
              <Button onClick={closeAddDialog} className="bg-blue-500 hover:bg-blue-500/90 text-white" data-testid="close-created-dialog">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent==='emerald' ? 'text-emerald-300' : accent==='blue' ? 'text-blue-300' : accent==='amber' ? 'text-amber-300' : 'text-white';
  return (
    <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function CopyRow({ label, value, isCopied, onCopy, testId }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={testId} className="flex-1 font-mono text-sm bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-emerald-300 break-all">
          {value}
        </code>
        <Button variant="outline" size="sm" onClick={onCopy} className="border-slate-700 text-slate-200 hover:text-white">
          {isCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <Copy className="w-4 h-4"/>}
        </Button>
      </div>
    </div>
  );
}
